import { GridHit, GridSpec } from "./USBAssignedUsersPDFGenerator";
import { NeoStroke } from '../../../neolab-libs/nl-lib3-common/neostroke/NeoStroke';

export type HitTestOptions = {
    /**
     * Input coordinate system. 'pdf' = PDF user space (origin bottom-left),
     * 'top-left' = screen/view coords (origin top-left). Defaults to 'pdf'.
     */
    coordinateSystem?: 'pdf' | 'top-left';
    /**
     * Scale factor from CSS px to PDF points when using 'top-left'. Defaults to 1.
     */
    scale?: number;
};

export class USBAssignedUserMapping {
    private spec: GridSpec;

    constructor(spec: GridSpec) {
        this.spec = spec;
    }

    private hitTestGridPoint(x: number, y: number, options: HitTestOptions = {}): GridHit {
        const pageWidth = this.spec.page.width;
        const pageHeight = this.spec.page.height;
        const { rows, cols, margins, gaps } = this.spec.grid;

        const coordinateSystem = options.coordinateSystem ?? 'pdf';
        const scale = options.scale && options.scale > 0 ? options.scale : 1;

        // Normalize to PDF user-space (origin bottom-left)
        let px = x;
        let py = y;
        if (coordinateSystem === 'top-left') {
            px = x / scale;
            py = pageHeight - y / scale;
        }

        const availableWidth = pageWidth - margins.left - margins.right - (cols - 1) * gaps.col;
        const availableHeight = pageHeight - margins.top - margins.bottom - (rows - 1) * gaps.row;
        if (availableWidth <= 0 || availableHeight <= 0) return null;

        const cellWidth = availableWidth / cols;
        const cellHeight = availableHeight / rows;

        const originX = margins.left;
        const originTopY = pageHeight - margins.top;

        const dx = px - originX;
        const dy = originTopY - py; // invert Y relative to grid top

        if (dx < 0 || dy < 0) return null;
        
        const col = Math.floor(dx / (cellWidth + gaps.col));
        const row = Math.floor(dy / (cellHeight + gaps.row));
        if (col < 0 || col >= cols || row < 0 || row >= rows) return null;

        const withinCol = (dx % (cellWidth + gaps.col)) <= cellWidth;
        const withinRow = (dy % (cellHeight + gaps.row)) <= cellHeight;
        if (!withinCol || !withinRow) return null; // hit the gap, not a cell

        const index = row * cols + col;
        return { index, row, col };
    }

    public mapStrokeFirstDotToGrid(stroke: NeoStroke): GridHit {
        if (!stroke || !stroke.dotArray || stroke.dotArray.length === 0) return null;
        const dot = stroke.dotArray[0];

        // Homography for mapping NeoStroke dot to PDF coordinates
        const homography = {
            a: 6.72,
            b: 0,
            c: 0,
            d: 0,
            e: 6.72,
            f: 0,
            g: 0,
            h: 0
        };

        const nom = homography.g * dot.x + homography.h * dot.y + 1;
        const px = (homography.a * dot.x + homography.b * dot.y + homography.c) / nom;
        const py = (homography.d * dot.x + homography.e * dot.y + homography.f) / nom;

        return this.hitTestGridPoint(px, py, { coordinateSystem: 'top-left', scale: 1 });
    }
}

